import { TGetRSOResDto } from "@/api/services/main/rso/dtos/get-rso.dto";
import { TUpdateRsoReqDto, TUpdateRsoResDto } from "@/api/services/main/rso/dtos/update-rso.dto";
import { TLinkVisaResDto, TSyncVisaResDto } from "@/api/services/main/rso/dtos/link-visa.dto";
import {
  TCancelRsoStudyLinkResDto,
  TCreateRsoStudyLinkReqDto,
  TCreateRsoStudyLinkResDto,
  TGetRsoStudyLinkRequest,
  TGetRsoStudyLinkResDto,
  TUnlinkRsoStudyLinkResDto,
} from "@/api/services/main/rso/dtos/study-link-request.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

const fetchRSOApi = async (): Promise<TGetRSOResDto> => {
  const { data } = await clientApi.get<TGetRSOResDto>("/rso");
  return data;
};

export const useRSO = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL],
    queryFn: () => fetchRSOApi(),
    enabled,
  });
};

// TODO: Replace mock with real API call
export const linkVisaApi = async (): Promise<TLinkVisaResDto> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ data: null, message: "irohana visaとの連携が完了しました" });
    }, 500);
  });
};

/**
 * ログイン中RSOの irohana visa 連携を解除する。
 * BE 側で Visa へ解除を依頼し、ローカルの有効連携 (rso_links) を解除確定する。
 */
export const unlinkRsoStudyLinkApi = async (): Promise<TUnlinkRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TUnlinkRsoStudyLinkResDto>("/rso/study-link-requests/unlink");
  return data;
};

/**
 * ログイン中RSOの認証待ち (pending) の連携申請を取り消す。
 * BE 側で Visa へ取消を依頼し、ローカルの申請ステータスを cancelled に確定する。
 */
export const cancelRsoStudyLinkRequestApi = async (): Promise<TCancelRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TCancelRsoStudyLinkResDto>("/rso/study-link-requests/cancel");
  return data;
};

/**
 * ログイン中RSOの有効連携の irohana visa 情報 (外国人数) を再同期する。
 * BE が Visa へ問い合わせて talentCount を取得・キャッシュ更新する。完了後 FE は RSO 詳細を再取得する。
 */
export const syncVisaInformationApi = async (): Promise<TSyncVisaResDto> => {
  const { data } = await clientApi.post<TSyncVisaResDto>("/rso/study-link-requests/sync");
  return data;
};

export const createRsoStudyLinkRequestApi = async (payload: TCreateRsoStudyLinkReqDto): Promise<TCreateRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TCreateRsoStudyLinkResDto>("/rso/study-link-requests", payload);
  return data;
};

/**
 * ログイン中RSOの現在の連携申請を irohana visa と状態同期する。
 * BE 側で Visa へ問い合わせて最新ステータスを取得・保存し、承認済みなら有効連携を作成する。
 * 申請が存在しない場合は data が null。
 */
export const getCurrentRsoStudyLinkApi = async (): Promise<TGetRsoStudyLinkRequest | null> => {
  const { data } = await clientApi.get<TGetRsoStudyLinkResDto>("/rso/study-link-requests");
  return data.data ?? null;
};

// 認証待ち中のポーリング間隔 (Visa 側で承認されたら自動で UI を更新するため)
const STUDY_LINK_SYNC_INTERVAL_MS = 8000;

/**
 * 認証待ち (pending) の間、Visa と状態を同期し続けるためのクエリ。
 * enabled が true の場合のみ実行し、一定間隔とウィンドウフォーカス時に再取得する。
 */
export const useSyncRsoStudyLink = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.SYNC_STUDY_LINK],
    queryFn: getCurrentRsoStudyLinkApi,
    enabled,
    refetchInterval: enabled ? STUDY_LINK_SYNC_INTERVAL_MS : false,
    refetchOnWindowFocus: enabled,
    gcTime: 0,
  });
};

/**
 * irohana visa 側の認証（アクセス承認）ページ URL を publicId から生成する。
 * ベース URL は REACT_APP_VISA_URL から取得。未設定の場合は null を返す。
 */
export const getVisaStudyAccessApprovalUrl = (publicId: string): string | null => {
  const visaBaseUrl = process.env.REACT_APP_VISA_URL;
  if (!visaBaseUrl || !publicId) return null;
  return `${visaBaseUrl.replace(/\/+$/, "")}/study/access-approval/${publicId}`;
};

export const updateRsoApi = async (payload: TUpdateRsoReqDto): Promise<TUpdateRsoResDto> => {
  const { data } = await clientApi.put<TUpdateRsoResDto>("/rso", payload);
  return data;
};
